import { useCallback, useEffect, useMemo, useState, useRef, JSX } from 'react'

import {
  Controller,
  FieldValues,
  Path,
  PathValue,
  useController,
  useWatch,
} from 'react-hook-form'
import { isAddress } from 'viem'
import { mainnet } from 'viem/chains'

import {
  AddressDisplay,
  type CLSize,
  Group,
  TokenDisplay,
} from '~/components/component-library'
import { AddressInputProps } from '~/components/component-library/AddressInput/types'
import { useENSLookup } from '~/components/component-library/AddressInput/useENSLookup'
import { ClearButton } from '~/components/inputs/ClearButton'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { AUTOCOMPLETE_SUPPRESS, Input } from '~/components/inputs/Input'
import { SupportedChainId } from '~/constants/chains'
import { hasENSDomain } from '~/hooks/useENS'
import { IAddress } from '~/types'
import { shortenAddress } from '~/utils/addresses'
import { isErc20OnChain } from '~/utils/erc20'
import { filledFieldFocusProps } from '~/utils/filledFieldFocus'
import { cn } from '~/utils/tailwind-utils'

// can expand this to include smart account type in the future
// if undefined, render it as an AddressDisplay
type AddressType = 'erc20' | 'standard'

// shown when a typed value is neither an ENS name nor a valid address
export const INVALID_ADDRESS_ERROR = 'Enter a valid address'

export const AddressInput = <FormType extends FieldValues>({
  control,
  inputName,
  placeholder = '0x… or vitalik.eth',
  setValue,
  setError,
  clearErrors,
  trigger,
  validationFunc = () => true,
  onClearInput,
  autoFocus,
  disabled = false,
  disabledReason,
  onENSValidityChange,
  onValidAddress,
  optional = false,
  requiredErrorMessage = 'Required',
  label,
  description,
  footer,
  tooltip,
  addressPopoverEditDisabled = false,
  chainId,
  size = 'base',
}: AddressInputProps<FormType>): JSX.Element => {
  const [addressEns, setAddressEns] = useState<string>()

  const inputVal = useWatch({
    control,
    name: inputName,
  })

  const { addressType, isCheckingAddressType } = useAddressType(
    inputVal,
    chainId,
  )

  const { fieldState, formState } = useController({ control, name: inputName })
  const error = fieldState.error
  const rhfIsValidating = fieldState.isValidating // async validation in RHF
  const errorText = visibleError(error, {
    isTouched: fieldState.isTouched,
    isEmpty: !inputVal,
    isSubmitted: formState.isSubmitted,
  })

  useEffect(() => {
    if (error?.message) {
      setAddressEns('')
    }
  }, [error?.message])

  const onValidEns = useCallback(
    (address: string) => {
      setAddressEns(inputVal)
      const typedAddress = address as PathValue<FormType, Path<FormType>>
      setValue(inputName, typedAddress, { shouldValidate: true })
      if (onENSValidityChange) {
        onENSValidityChange({ valid: true, ens: inputVal })
      }
    },
    [inputVal, setValue, inputName, onENSValidityChange],
  )

  const onInvalidEns = useCallback(() => {
    setAddressEns('')
    setError(inputName, {
      type: 'ensFailure',
      message: `ENS not found`,
    })
    if (onENSValidityChange) {
      onENSValidityChange({ valid: false })
    }
  }, [inputName, setError, onENSValidityChange])

  useEffect(() => {
    // Clear ENS error when input is empty
    if (!inputVal && error?.type === 'ensFailure') {
      clearErrors(inputName)
    }
  }, [inputVal, error?.type, inputName, clearErrors])

  const onValidAddressWithEns = useCallback(
    (ens: string) => {
      if (ens === addressEns) return
      setAddressEns(ens)
    },
    [addressEns],
  )

  const isLookingUpENS = useENSLookup(inputVal, mainnet.id, {
    onValidEns,
    onInvalidEns,
    onValidAddressWithEns,
  })

  const inputRef = useRef<HTMLInputElement>(null)

  // When a valid address swaps the input for the filled display mid-edit,
  // keep the field focused by moving focus to the mounting container; a value
  // arriving while the user is elsewhere (prefill, setValue) must not steal
  // focus. The flag survives the input's unmount (no blur fires on removal).
  const inputHadFocusRef = useRef(false)
  const focusOnSwap = useCallback((el: HTMLDivElement | null) => {
    if (el && inputHadFocusRef.current) {
      inputHadFocusRef.current = false
      el.focus()
    }
  }, [])

  const clearInput = useCallback(() => {
    const typedAddress = '' as PathValue<FormType, Path<FormType>>
    setValue(inputName, typedAddress, { shouldValidate: true })
    setAddressEns('')
    setHasBeenEdited(true)
    lastPassedAddressRef.current = undefined
    if (onClearInput) onClearInput()
    // Focus the input once it mounts in place of the filled display
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [inputName, onClearInput, setValue])

  const addressIsValid = useMemo(
    () =>
      isAddress(inputVal) &&
      !error &&
      !rhfIsValidating &&
      !isLookingUpENS &&
      !isCheckingAddressType,
    [inputVal, error, rhfIsValidating, isLookingUpENS, isCheckingAddressType],
  )

  // used to prevent onValidAddress being called on the defaultValue
  const [hasBeenEdited, setHasBeenEdited] = useState(false)
  // stores the last passed address to prevent duplicate onValidAddress calls
  const lastPassedAddressRef = useRef<string | undefined>(undefined)

  /**
   * Effect to pass up each valid address typed into the input (after the defaultValue is passed down)
   * Clearing the input (which resets `lastPassedAddressRef.current`) allows the same address to be re-validated if entered again.
   */
  useEffect(() => {
    if (
      hasBeenEdited &&
      isAddress(inputVal) &&
      !rhfIsValidating &&
      onValidAddress &&
      addressIsValid &&
      inputVal !== lastPassedAddressRef.current // Only call if the valid address is new
    ) {
      onValidAddress(inputVal)
      lastPassedAddressRef.current = inputVal // Remember the last passed value
    }
  }, [
    inputVal,
    error,
    isLookingUpENS,
    rhfIsValidating,
    hasBeenEdited,
    onValidAddress,
    addressIsValid,
  ])

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field }) => (
        <Group
          label={label}
          secondaryLabel={optional ? 'Optional' : undefined}
          footer={errorText ? <ErrorFooter message={errorText} /> : footer}
          description={description}
          tooltip={tooltip}
        >
          {addressIsValid ? (
            <div
              ref={focusOnSwap}
              {...filledFieldFocusProps({
                filled: true,
                disabled,
                onClear: clearInput,
              })}
              className={cn(
                'relative grid w-full grid-cols-[1fr_auto] items-center space-x-2 rounded-lg bg-white outline-none ring-1 ring-inset ring-gray-200 transition-shadow focus-within:ring-2 focus-within:ring-blue-500 dark:bg-black dark:ring-gray-700',
                {
                  'py-1.5 pl-3 pr-1.5': size === 'small',
                  'py-2.5 pl-3.5 pr-2.5': size === 'base',
                },
              )}
            >
              <DisplaySwitcher
                address={field.value!}
                addressPopoverEditDisabled={addressPopoverEditDisabled}
                addressType={addressType}
                chainId={chainId!}
                size={size}
              />
              {disabled ? (
                <div
                  className={cn({
                    'size-5': size === 'small',
                    'size-6': size === 'base',
                  })}
                />
              ) : (
                <ClearButton onClick={clearInput} inputSize={size} />
              )}
            </div>
          ) : (
            <Input
              ref={inputRef}
              placeholder={placeholder}
              autoComplete={AUTOCOMPLETE_SUPPRESS}
              disabled={disabled}
              disabledReason={disabledReason}
              autoFocus={autoFocus}
              value={field.value ?? ''}
              onChange={(e) => {
                const newValue = e.target.value.replace(/\s/g, '')
                field.onChange(newValue)
                // only validate eagerly when the value looks complete — a
                // partial address shouldn't surface a format error mid-keystroke
                if (isAddress(newValue) || hasENSDomain(newValue)) {
                  trigger(inputName)
                }
                setHasBeenEdited(true)
              }}
              onFocus={() => {
                inputHadFocusRef.current = true
              }}
              onBlur={() => {
                inputHadFocusRef.current = false
                field.onBlur()
              }}
              onClearInput={inputVal || onClearInput ? clearInput : undefined}
              error={errorText}
              showError={false}
              message={
                !isLookingUpENS && !rhfIsValidating && isAddress(inputVal)
                  ? addressEns
                    ? shortenAddress(inputVal)
                    : 'Valid address'
                  : undefined
              }
              isLoading={
                isLookingUpENS || rhfIsValidating || isCheckingAddressType
              }
              disableWhenLoading={false}
              inputSize={size}
            />
          )}
        </Group>
      )}
      rules={{
        required: {
          value: !optional,
          message: requiredErrorMessage,
        },
        validate: (value: string) => {
          // ENS names resolve asynchronously (failures reported via setError),
          // and empty is owned by `required`. Anything else must be a real
          // address, so a typed-but-invalid value errors on blur.
          if (value && !hasENSDomain(value) && !isAddress(value)) {
            return INVALID_ADDRESS_ERROR
          }
          return validationFunc(value)
        },
      }}
    />
  )
}

AddressInput.displayName = 'AddressInput'

function useAddressType(inputVal: string, chainId?: SupportedChainId) {
  const [addressType, setAddressType] = useState<AddressType>()
  const [isCheckingAddressType, setIsCheckingAddressType] = useState(false)

  useEffect(() => {
    // Only check for ERC-20 if chainId is provided
    if (!chainId || !isAddress(inputVal) || hasENSDomain(inputVal)) {
      setAddressType(undefined)
      setIsCheckingAddressType(false)
      return
    }

    let cancelled = false
    setAddressType('standard')
    setIsCheckingAddressType(true)

    isErc20OnChain(inputVal as IAddress, chainId).then((isErc20) => {
      if (cancelled) return

      setAddressType(isErc20 ? 'erc20' : 'standard')
      setIsCheckingAddressType(false)
    })

    return () => {
      cancelled = true
    }
  }, [inputVal, chainId])

  return { addressType, isCheckingAddressType }
}

const DisplaySwitcher = ({
  address,
  chainId,
  addressPopoverEditDisabled,
  addressType,
  size,
}: {
  address: IAddress
  chainId?: SupportedChainId
  addressPopoverEditDisabled?: boolean
  addressType?: AddressType
  size: CLSize
}) => {
  if (addressType === 'erc20') {
    if (!chainId) throw new Error('Chain ID is required for ERC20 addresses')
    return (
      <TokenDisplay
        address={address}
        chainId={chainId}
        layout="standard"
        size={size}
      />
    )
  }

  return (
    <AddressDisplay
      address={address}
      layout="standard"
      popoverEditDisabled={addressPopoverEditDisabled}
      size={size}
    />
  )
}

export type { AddressInputProps } from '~/components/component-library/AddressInput/types'
